import React, { useEffect, useState } from "react";
import { X, Send, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const fmtChf = (n) => {
  const v = Number(n || 0);
  return Math.abs(v - Math.round(v)) < 0.005
    ? Math.round(v).toLocaleString("de-CH").replace(/,/g, "'") + ".–"
    : v.toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/,/g, "'");
};

const OwnerNotifyModal = ({ open, contract, onClose, onSent }) => {
  const [pct, setPct] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [includePdf, setIncludePdf] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !contract) return;
    // load placeholder owner for this vehicle
    let cancelled = false;
    api.get(`/vehicles/${contract.vehicle_id}/owner`)
      .then((r) => { if (!cancelled) { setName(r.data?.name || ""); setEmail(r.data?.email || ""); } })
      .catch(() => {});
    // if a previous notification exists, prefill from it
    const prev = contract.owner_payout;
    if (prev) {
      setPct(prev.commission_pct ?? "");
      if (prev.owner_name) setName(prev.owner_name);
      if (prev.owner_email) setEmail(prev.owner_email);
    } else {
      setPct("");
    }
    setNote("");
    setIncludePdf(false);
    return () => { cancelled = true; };
  }, [open, contract]);

  if (!open || !contract) return null;

  const rent = Number(contract.price_chf || 0);
  const safePct = Math.max(0, Math.min(100, Number(pct) || 0));
  const commission = Math.round((rent * safePct) / 100 * 100) / 100;
  const payout = Math.round((rent - commission) * 100) / 100;

  const canSend = email && !email.includes("example.ch") && safePct >= 0;

  const send = async () => {
    if (!canSend) {
      toast.error("Bitte echte Owner-E-Mail eintragen (nicht …@example.ch)");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/contracts/${contract.id}/notify-owner`, {
        commission_pct: safePct,
        name: name || null,
        email,
        include_pdf: includePdf,
        note: note || null,
      });
      toast.success("Besitzer wurde per E-Mail benachrichtigt");
      onSent && onSent();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Versand fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="owner-notify-modal">
      <div className="bg-white w-full sm:max-w-2xl max-h-[95vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl">
        <div className="flex items-center justify-between p-5 border-b border-[var(--rs-border)] sticky top-0 bg-white z-10">
          <div>
            <div className="rs-overline">Besitzer benachrichtigen</div>
            <h2 className="font-[Chivo] text-2xl font-black mt-1">{contract.contract_number}</h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-[var(--rs-surface)] rounded-full">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 sm:p-7 space-y-6">
          {/* Vehicle + period summary */}
          <div className="bg-[var(--rs-surface)] border-l-4 border-[var(--rs-primary)] p-4">
            <div className="text-sm font-bold">{contract.vehicle_name} · {contract.vehicle_plate}</div>
            <div className="text-xs text-[var(--rs-text-muted)] mt-1">
              {(contract.rental_start || "").replace("T", " · ")} → {(contract.rental_end || "").replace("T", " · ")}
            </div>
            {(contract.customer?.full_name || contract.customer_name) && (
              <div className="text-xs mt-2"><span className="text-[var(--rs-text-muted)]">Mieter: </span>{contract.customer?.full_name || contract.customer_name}</div>
            )}
          </div>

          {/* Owner contact */}
          <section>
            <div className="rs-overline mb-3">Fahrzeug-Besitzer</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="rs-label">Name</span>
                <input className="rs-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Vor- und Nachname" data-testid="owner-name" />
              </label>
              <label className="block">
                <span className="rs-label">E-Mail</span>
                <input type="email" className="rs-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="owner@example.ch" data-testid="owner-email" />
              </label>
            </div>
            {email.includes("example.ch") && (
              <div className="text-xs text-[#B91C1C] mt-2">⚠️ Platzhalter-E-Mail — bitte echte Adresse eintragen.</div>
            )}
          </section>

          {/* Commission + payout calculation */}
          <section>
            <div className="rs-overline mb-3">Vergütung</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="rs-label">Provision NIVARA in %</span>
                <input type="number" min="0" max="100" step="0.5" className="rs-input"
                  value={pct} onChange={(e) => setPct(e.target.value)} placeholder="z. B. 15" data-testid="commission-pct" />
              </label>
              <div className="bg-white border border-[var(--rs-border)] rounded-xl p-4">
                <div className="flex justify-between text-sm py-1">
                  <span className="text-[var(--rs-text-muted)]">Mietbetrag</span>
                  <span className="font-bold">CHF {fmtChf(rent)}</span>
                </div>
                <div className="flex justify-between text-sm py-1">
                  <span className="text-[var(--rs-text-muted)]">Provision ({safePct}%)</span>
                  <span className="text-[#B91C1C]">− CHF {fmtChf(commission)}</span>
                </div>
                <div className="flex justify-between text-base font-black border-t border-[var(--rs-border)] pt-2 mt-2 text-[var(--rs-primary)]">
                  <span>Auszahlung</span>
                  <span data-testid="payout-amount">CHF {fmtChf(payout)}</span>
                </div>
              </div>
            </div>
          </section>

          {/* Note + include PDF */}
          <section>
            <label className="block">
              <span className="rs-label">Notiz für Besitzer (optional)</span>
              <textarea className="rs-input min-h-[80px]" value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="z. B. Tank voll bei Rückgabe, Schlüssel-Übergabe geplant um 09:00…"
                data-testid="owner-note" />
            </label>
            <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer">
              <input type="checkbox" checked={includePdf} onChange={(e) => setIncludePdf(e.target.checked)} data-testid="include-pdf" />
              <span>Mietvertrag-PDF mitsenden</span>
            </label>
          </section>

          {contract.owner_payout?.notified_at && (
            <div className="text-xs text-[var(--rs-text-muted)] bg-[var(--rs-surface)] p-3 rounded-xl">
              Zuletzt benachrichtigt: {new Date(contract.owner_payout.notified_at).toLocaleString("de-CH")} ·
              Auszahlung damals CHF {fmtChf(contract.owner_payout.payout_chf)}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 p-5 border-t border-[var(--rs-border)] sticky bottom-0 bg-white">
          <button type="button" onClick={onClose} className="rs-btn-ghost !text-xs">
            Abbrechen
          </button>
          <button type="button" onClick={send} disabled={!canSend || busy}
            className="rs-btn-primary !text-xs disabled:opacity-40"
            data-testid="owner-send-btn">
            <Send size={14} className="mr-2" />
            {busy ? "Sende…" : "E-Mail an Besitzer senden"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OwnerNotifyModal;
